// src/PersonalFinance.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { LanguageContext } from "./LanguageContext";
import { exportCourseDocx, openIosStagingTab } from "./utils/docxExport";
import { useIsPaidUser } from "./hooks/useIsPaidUser";
import "./css/PersonalFinance.css";
import "./css/InvestparaBr.css";

const apiUrl = "https://qedeefqyh2.execute-api.us-west-2.amazonaws.com/prod";

// ===== DEV BYPASS =====
const isDev = process.env.NODE_ENV === "development";
const authEnabled = false; // disable auth checks on localhost/dev

const PersonalFinance = () => {
  const history = useHistory();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { language, translations } = useContext(LanguageContext);
  const [downloading, setDownloading] = useState(false);
  const [showIntro, setShowIntro] = useState(false);

  // Paid guard
  const { isPaidUser, isCheckingPaid } = useIsPaidUser("personal-finance");

  // UI: which module is open
  const [showModule, setShowModule] = useState({ 0: true });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ------- Course Data (modules 0-5) -------
  const personalFinanceCourses = useMemo(
    () => [
      {
        module: 0,
        lessons: [
          { id: 1, globalId: 1, title: translations.whyPersonalFinance },
          { id: 2, globalId: 2, title: translations.knowRiskLimit },
          { id: 3, globalId: 3, title: translations.investments },
        ],
      },
      {
        module: 1,
        lessons: [
          { id: 1, globalId: 4, title: translations.waysInvest },
          { id: 2, globalId: 5, title: translations.differentStockThinking },
          { id: 3, globalId: 6, title: translations.buyingSellingStocks },
          { id: 4, globalId: 7, title: translations.marketTimingExamples },
          { id: 5, globalId: 8, title: translations.sellingStocks },
          { id: 6, globalId: 9, title: translations.stocksHaveHomes1 },
          { id: 7, globalId: 10, title: translations.stocksHaveHomes2 },
          { id: 8, globalId: 11, title: translations.whereToFindStocks },
          { id: 9, globalId: 12, title: translations.priceDiscovery },
          { id: 10, globalId: 13, title: translations.mutualFunds },
          { id: 11, globalId: 14, title: translations.vanguardGrowth },
          { id: 12, globalId: 15, title: translations.vanguard500 },
        ],
      },
      {
        module: 2,
        lessons: [
          { id: 1, globalId: 16, title: translations.financialRatios },
          { id: 2, globalId: 17, title: translations.etfs },
          { id: 3, globalId: 18, title: translations.stockIndices },
          { id: 4, globalId: 19, title: translations.stockAnalysis },
          { id: 5, globalId: 20, title: translations.bonds },
          { id: 6, globalId: 21, title: translations.bondPricing },
          { id: 7, globalId: 22, title: translations.bondEarnings },
          { id: 8, globalId: 23, title: translations.stockInstruments },
          { id: 9, globalId: 24, title: translations.marginShortSelling },
          { id: 10, globalId: 25, title: translations.options1 },
          { id: 11, globalId: 26, title: translations.options2 },
        ],
      },
      {
        module: 3,
        lessons: [
          { id: 1, globalId: 27, title: translations.insuranceDefensiveStrategy },
          { id: 2, globalId: 28, title: translations.insuranceVsSpeculation },
          { id: 3, globalId: 29, title: translations.oddsMakerUnderwriting },
          { id: 4, globalId: 30, title: translations.insuranceEconomics },
          { id: 5, globalId: 31, title: translations.termInsurance },
          { id: 6, globalId: 32, title: translations.universalLife },
          { id: 7, globalId: 33, title: translations.needInsurance },
          { id: 8, globalId: 34, title: translations.miscellaneousInsurance },
          { id: 9, globalId: 35, title: translations.viaticalInsurance },
          { id: 10, globalId: 36, title: translations.propertyInsurance },
          { id: 11, globalId: 37, title: translations.homeownerInsurance },
          { id: 12, globalId: 38, title: translations.automobileInsurance },
          { id: 13, globalId: 39, title: translations.autoInsuranceClaims },
          { id: 14, globalId: 40, title: translations.autoInsurancePremiums },
          { id: 15, globalId: 41, title: translations.decreasePremium },
          { id: 16, globalId: 42, title: translations.intuitionInsuranceResearch },
        ],
      },
      {
        module: 4,
        lessons: [
          { id: 1, globalId: 43, title: translations.roleOfCredit },
          { id: 2, globalId: 44, title: translations.differentFacesOfCredit },
          { id: 3, globalId: 45, title: translations.creditHouseholdDebtParadox },
          { id: 4, globalId: 46, title: translations.isAllCreditEqual },
          { id: 5, globalId: 47, title: translations.deadbeatsVsRevolvers },
          { id: 6, globalId: 48, title: translations.whatLendersLookFor },
          { id: 7, globalId: 49, title: translations.creditBureaus },
          { id: 8, globalId: 50, title: translations.creditBureauInformation },
          { id: 9, globalId: 51, title: translations.ficoScore },
          { id: 10, globalId: 52, title: translations.creditReportGuts },
          { id: 11, globalId: 53, title: translations.bankruptcy },
          { id: 12, globalId: 54, title: translations.creditRationing },
        ],
      },
      {
        module: 5,
        lessons: [
          { id: 1, globalId: 55, title: translations.startEarlyRetirement },
          { id: 2, globalId: 56, title: translations.threeLeggedStool },
          { id: 3, globalId: 57, title: translations.socialSecurity },
          { id: 4, globalId: 58, title: translations.historyOfSocialSecurity },
          { id: 5, globalId: 59, title: translations.socialSecurityReserves },
          { id: 6, globalId: 60, title: translations.pensions },
          { id: 7, globalId: 61, title: translations.definedContributions },
          { id: 8, globalId: 62, title: translations.chooseRetirementPlan },
          { id: 9, globalId: 63, title: translations.vesting },
          { id: 10, globalId: 64, title: translations.retirementBehaviorResearch },
          { id: 11, globalId: 65, title: translations.lastLegRetirement },
          { id: 12, globalId: 66, title: translations.rothIRA },
          { id: 13, globalId: 67, title: translations.traditionalVsRoth },
          { id: 14, globalId: 68, title: translations.improvingRetirementSavingsBehavior },
          { id: 15, globalId: 69, title: translations.conclusion },
        ],
      },
    ],
    [translations]
  );

  // ------- Navigation -------
  const handleLessonClick = (module, lessonId) => {
    if (authEnabled) {
      const isPersonalPremiumModule = module > 0 && module < 6;

      if (!isAuthenticated) {
        alert(translations.fullAcess);
        history.push("/premium");
        return;
      }
      if (!isCheckingPaid && !isPaidUser && isPersonalPremiumModule) {
        alert(translations.fullAcess);
        history.push("/premium");
        return;
      }
    }

    history.push(`/lessons/module${module}/lesson${lessonId}?lang=${language}&course=personal-finance`);
  };

  const buildPFRefs = () =>
    personalFinanceCourses
      .flatMap(({ module, lessons }) =>
        lessons.map(({ id }) => ({
          moduleKey: `module${module}`,
          lessonKey: `lesson${id}`,
          id,
        }))
      )
      .sort((a, b) => {
        const mA = parseInt(a.moduleKey.replace("module", ""), 10);
        const mB = parseInt(b.moduleKey.replace("module", ""), 10);
        return mA === mB ? a.id - b.id : mA - mB;
      });

  const handleDownload = async () => {
    const preWin = openIosStagingTab();
    try {
      if (authEnabled) {
        if (!isAuthenticated) {
          localStorage.setItem("auth_redirect_path", window.location.pathname);
          await loginWithRedirect();
          if (preWin) preWin.close();
          return;
        }
        if (!isCheckingPaid && !isPaidUser) {
          alert(translations.fullAcess);
          history.push("/premium");
          if (preWin) preWin.close();
          return;
        }
      }

      setDownloading(true);
      const refs = buildPFRefs();
      const docTitle =
        language === "pt" ? "Finanças Pessoais - PT" : "Personal Finance - EN";
      const fileBase =
        language === "pt" ? "financas-pessoais-PT" : "personal-finance-EN";

      await exportCourseDocx({
        refs,
        apiUrl,
        language,
        docTitle,
        fileBase,
        labels: {
          courseIntro: translations.courseContent || "",
          moduleLabel: translations.module || "Module",
          showModuleHeadings: true,
          renumberSingleModuleTo1: false,
        },
        preOpenedWin: preWin,
      });
    } catch (e) {
      console.error(e);
      alert("Sorry — couldn't create the Word file.");
      if (preWin) preWin.close();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="investpara-page">
      <div className="courses-page1">
        <h1 className="investpara-title">
          {language === "pt" ? (
            <>Finanças <span>Pessoais</span></>
          ) : (
            <>Personal <span>Finance</span></>
          )}
        </h1>

        {/* ===== INTRO CARD ===== */}
        <section className="investpara-introCard">
          <span className="course-badge course-badge--trial" style={{ marginBottom: 12 }}>
            {language === "pt" ? "Módulo 0 Grátis" : "Module 0 Free"}
          </span>
          <h2 className="investpara-subtitle">
            {language === "pt"
              ? "Domine suas Finanças e Construa seu Futuro"
              : "Master Your Finances and Build Your Future"}
          </h2>

          <p className="investpara-lead">
            {language === "pt"
              ? "Um curso completo sobre finanças pessoais: desde os fundamentos de investimentos até aposentadoria, seguros e crédito."
              : "A comprehensive course on personal finance: from investment fundamentals to retirement, insurance, and credit."}
          </p>

          {/* Collapsible content */}
          <div
            className={`investpara-collapsible ${showIntro ? "open" : "closed"}`}
            aria-hidden={!showIntro}
          >
            <p className="investpara-paragraph">
              {language === "pt"
                ? "Este curso aborda todos os pilares das finanças pessoais: como investir, entender o mercado de ações, fundos mútuos, ETFs, seguros, crédito e planejamento para aposentadoria."
                : "This course covers all pillars of personal finance: how to invest, understand the stock market, mutual funds, ETFs, insurance, credit, and retirement planning."}
            </p>

            <div className="investpara-block">
              <h3>{language === "pt" ? "Ao final do curso, você será capaz de:" : "By the end of the course, you will be able to:"}</h3>
              <ul>
                <li>{language === "pt" ? "Entender os fundamentos de investimentos e mercado de ações" : "Understand investment fundamentals and the stock market"}</li>
                <li>{language === "pt" ? "Analisar diferentes instrumentos financeiros (ações, bonds, ETFs, opções)" : "Analyze different financial instruments (stocks, bonds, ETFs, options)"}</li>
                <li>{language === "pt" ? "Compreender os tipos de seguros e quando utilizá-los" : "Understand types of insurance and when to use them"}</li>
                <li>{language === "pt" ? "Gerenciar crédito de forma inteligente" : "Manage credit wisely"}</li>
                <li>{language === "pt" ? "Planejar sua aposentadoria com estratégias eficazes" : "Plan your retirement with effective strategies"}</li>
              </ul>
            </div>

            <div className="investpara-grid2">
              <div className="investpara-block">
                <h3>{language === "pt" ? "Este curso é para você se:" : "This course is for you if:"}</h3>
                <ul>
                  <li>{language === "pt" ? "Quer entender como funcionam os investimentos" : "You want to understand how investments work"}</li>
                  <li>{language === "pt" ? "Deseja tomar decisões financeiras mais informadas" : "You want to make more informed financial decisions"}</li>
                  <li>{language === "pt" ? "Quer planejar seu futuro financeiro com segurança" : "You want to plan your financial future securely"}</li>
                </ul>
              </div>

              <div className="investpara-block">
                <h3>{language === "pt" ? "Este curso NÃO é para você se:" : "This course is NOT for you if:"}</h3>
                <ul>
                  <li>{language === "pt" ? "Procura esquemas de enriquecimento rápido" : "You're looking for get-rich-quick schemes"}</li>
                  <li>{language === "pt" ? "Não tem interesse em aprender os fundamentos" : "You're not interested in learning the fundamentals"}</li>
                  <li>{language === "pt" ? "Quer dicas de investimentos específicos" : "You want specific investment tips"}</li>
                </ul>
              </div>
            </div>

            <div className="investpara-block">
              <h3>{language === "pt" ? "O que está incluso no curso" : "What's included in the course"}</h3>

              <p style={{ margin: "6px 0 12px 0", opacity: 0.9 }}>
                <strong>{language === "pt" ? "6 módulos" : "6 modules"}</strong> •{" "}
                <strong>{language === "pt" ? "69 aulas" : "69 lessons"}</strong> •{" "}
                <strong>{language === "pt" ? "Idioma:" : "Language:"}</strong> {language === "pt" ? "Português e Inglês" : "Portuguese and English"}
              </p>

              <ul>
                <li>{language === "pt" ? "Módulo 0: Introdução às Finanças Pessoais" : "Module 0: Introduction to Personal Finance"}</li>
                <li>{language === "pt" ? "Módulo 1: Investimentos e Mercado de Ações" : "Module 1: Investments and Stock Market"}</li>
                <li>{language === "pt" ? "Módulo 2: Instrumentos Financeiros Avançados" : "Module 2: Advanced Financial Instruments"}</li>
                <li>{language === "pt" ? "Módulo 3: Seguros" : "Module 3: Insurance"}</li>
                <li>{language === "pt" ? "Módulo 4: Crédito" : "Module 4: Credit"}</li>
                <li>{language === "pt" ? "Módulo 5: Aposentadoria" : "Module 5: Retirement"}</li>
              </ul>
            </div>

            <div style={{ marginTop: 14 }}>
              <Link
                to="/premium"
                className="investpara-ctaBtn"
              >
                {language === "pt" ? "Comprar o curso" : "Buy the course"}
              </Link>

              <div className="investpara-ctaSubtext">
                {language === "pt"
                  ? "Compra sem risco: se não fizer sentido para você, devolvemos seu dinheiro."
                  : "Risk-free purchase: if it doesn't work for you, we'll refund your money."}
              </div>
            </div>

            <div className="investpara-block">
              <h3>{language === "pt" ? "Perguntas frequentes" : "Frequently asked questions"}</h3>

              <p>
                <strong>{language === "pt" ? "Preciso ter experiência prévia?" : "Do I need prior experience?"}</strong><br />
                {language === "pt"
                  ? "Não. O curso começa do zero e evolui progressivamente."
                  : "No. The course starts from scratch and progresses gradually."}
              </p>

              <p>
                <strong>{language === "pt" ? "O curso dá dicas de investimentos?" : "Does the course give investment tips?"}</strong><br />
                {language === "pt"
                  ? "Não. O foco é educação financeira para você tomar suas próprias decisões."
                  : "No. The focus is financial education so you can make your own decisions."}
              </p>

              <p>
                <strong>{language === "pt" ? "Posso acessar o conteúdo para sempre?" : "Can I access the content forever?"}</strong><br />
                {language === "pt"
                  ? "Sim. Você terá acesso vitalício ao conteúdo do curso."
                  : "Yes. You will have lifetime access to the course content."}
              </p>
            </div>
          </div>

          {/* Show more/less button */}
          <button
            type="button"
            className="investpara-toggleLink"
            onClick={() => setShowIntro((v) => !v)}
            aria-expanded={showIntro}
          >
            {showIntro
              ? (language === "pt" ? "Ver menos ↑" : "Show less ↑")
              : (language === "pt" ? "Ver mais sobre o curso ↓" : "Show more about the course ↓")}
          </button>
        </section>

        {/* ===== MODULES ===== */}
        {personalFinanceCourses.map(({ module, lessons }) => {
          const open = !!showModule[module];
          const toggle = () =>
            setShowModule((s) => ({ ...s, [module]: !s[module] }));

          return (
            <div className="pf-module module" key={module}>
              <h2
                onClick={toggle}
                className="toggle-heading"
                role="button"
                aria-expanded={open}
                tabIndex={0}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && toggle()
                }
              >
                {translations.module} {module}
                {module === 0 && ` - ${translations.introduction}`}
                <span className={`arrow ${open ? "open" : ""}`}>&gt;</span>
              </h2>

              {open && (
                <ul>
                  {lessons.map((lesson) => (
                    <li key={lesson.id} className="lesson-row">
                      <button
                        onClick={() => handleLessonClick(module, lesson.id)}
                        className="lesson-link1"
                      >
                        {lesson.id}. {lesson.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}

        {/* ===== DOWNLOAD ===== */}
        <h3 style={{ marginTop: 16 }}>{translations.download}</h3>
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="lesson-link"
          style={{ padding: "8px 14px", borderRadius: 8 }}
        >
          {downloading
            ? translations.preparing || "Preparing…"
            : translations.downloadWord || "Download Word (.docx)"}
        </button>
      </div>
    </div>
  );
};

export default PersonalFinance;
